import { useEffect, useMemo, useState } from 'react';
import { Box, css } from '@mui/material';

import { FilterExpression, FilterExpressionHelpers, TimeFilter, TimePrecision } from '@playq/octopus-common';

import { DateTimePicker } from '/shared/DateTimePicker';
import { DateRange, DateRangePicker } from '/shared/DateRangePicker';
import { IFilterCellProps } from '/shared/Table/components/TableHeadCell/IFilterCellProps';
import { FilterExpressionsSelect } from '/shared/Table/components/TableHeadCell/FilterExpressionsSelect';
import { datePickerCSS } from '/shared/Table/components/TableHeadCell';

export function TimeFilterCell({ disabled, filter, onSubmit, withTimePresets = true }: IFilterCellProps) {
  const timeFilterCurrent = filter as TimeFilter;

  const [dateRange, setDateRange] = useState<DateRange>(() => {
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (!timeFilterCurrent) {
      return [null, null];
    }

    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    return [timeFilterCurrent.left || null, timeFilterCurrent.right || null];
  });

  const [expression, setExpression] = useState(() => {
    if (!filter) {
      return FilterExpressionHelpers.all[0];
    }

    return timeFilterCurrent.expression;
  });

  const isRight = useMemo(
    () => expression === FilterExpression.OutOfRange || expression === FilterExpression.InRange,
    [expression]
  );

  const handleSingleChange = (date: Date) => {
    setDateRange([date, null]);
  };

  const handleDateRangeChange = (nextDateRange: DateRange) => {
    setDateRange(nextDateRange);
  };

  useEffect(() => {
    if (!dateRange[0] || (isRight && !dateRange[1])) {
      return;
    }

    const timeFilter = new TimeFilter();

    const [left, right] = dateRange;
    timeFilter.left = left;

    if (isRight) {
      timeFilter.right = right || undefined;
    }

    timeFilter.expression = expression;
    timeFilter.precision = TimePrecision.Day;

    setTimeout(() => {
      onSubmit(timeFilter, false);
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, isRight, expression]);

  return (
    <div>
      <FilterExpressionsSelect
        styles={{
          formGroup: css`
            padding: 8px 8px 0 8px;
          `,
        }}
        initialExp={expression}
        disabled={disabled}
        onChange={setExpression}
      />

      {!isRight ? (
        <DateTimePicker
          disabled={disabled}
          date={dateRange[0] ?? new Date()}
          onChange={handleSingleChange}
          styles={{ datePicker: datePickerCSS }}
        />
      ) : (
        <Box sx={{ padding: '8px' }}>
          <DateRangePicker
            disabled={disabled}
            dateRange={dateRange}
            onDateRangeChange={handleDateRangeChange}
            withTimePresets={withTimePresets}
          />
        </Box>
      )}
    </div>
  );
}

TimeFilterCell.type = TimeFilter.ClassName;
